import { PipelineStage } from './types'

export interface PipelineTab {
    id: string
    label: string
    stage: PipelineStage | string
    step_id?: string | null
    step_type: string
}

export interface SeasonPipelineDefinitionResponse {
    definition: Record<string, unknown> | null
    tabs: PipelineTab[]
}

/**
 * Demo / ops tabs for Leads Pipeline. Config-derived tabs are ignored in the
 * pipeline UI for now; sub-statuses live inside each tab (not as extra tabs).
 */
export const DEFAULT_PIPELINE_TABS: PipelineTab[] = [
    { id: 'leads', label: 'Leads', stage: 'leads', step_type: 'leads' },
    {
        id: 'app_sent',
        label: 'Applications',
        stage: 'app_sent',
        step_type: 'application',
    },
    {
        id: 'screening',
        label: 'Screening',
        stage: 'screening',
        step_type: 'screening',
    },
    {
        id: 'leases',
        label: 'Leases',
        stage: 'leases',
        step_type: 'lease',
    },
    {
        id: 'future_resident',
        label: 'Future Resident',
        stage: 'future_resident',
        step_type: 'future_resident',
    },
]

/** Map a pipeline tab id to the coarse stage used for table columns. */
export function tabIdToColumnStage(
    tabId: string,
    tab?: PipelineTab,
): PipelineStage {
    if (
        tabId === 'leases' ||
        tabId === 'lease_sent' ||
        tabId === 'lease_signed'
    ) {
        return 'leases'
    }
    if (tab?.stage === 'leases') {
        return 'leases'
    }
    if (tab?.stage && isPipelineStage(tab.stage)) {
        if (tab.stage === 'app_sent') {
            return 'app_sent'
        }
        return tab.stage
    }

    if (tabId === 'app_sent') {
        return 'app_sent'
    }
    if (tabId.startsWith('step_screening') || tabId === 'screening') {
        return 'screening'
    }
    if (isPipelineStage(tabId)) {
        return tabId
    }
    return 'leads'
}

function isPipelineStage(value: string): value is PipelineStage {
    return (
        value === 'leads' ||
        value === 'tours' ||
        value === 'app_sent' ||
        value === 'screening' ||
        value === 'leases' ||
        value === 'lease_sent' ||
        value === 'lease_signed' ||
        value === 'guarantor' ||
        value === 'future_resident'
    )
}

export function resolveLeadTabId(
    lead: { pipeline_stage: string; pipeline_step_id?: string | null },
    tabs: PipelineTab[],
): string | null {
    for (const tab of tabs) {
        if (tab.id === 'leads' && lead.pipeline_stage === 'leads') {
            return tab.id
        }

        if (tab.step_type === 'application' || tab.id === 'app_sent') {
            if (
                lead.pipeline_stage === 'app_sent' ||
                lead.pipeline_stage === 'app_signed_pending_review'
            ) {
                if (
                    tab.step_id &&
                    lead.pipeline_step_id &&
                    lead.pipeline_step_id !== tab.step_id
                ) {
                    continue
                }
                return tab.id
            }
            continue
        }

        if (tab.step_type === 'screening') {
            if (lead.pipeline_stage !== 'screening') {
                continue
            }
            if (tab.id === 'screening') {
                return tab.id
            }
            if (lead.pipeline_step_id === tab.id) {
                return tab.id
            }
            const firstScreeningTab = tabs.find(
                (t) => t.step_type === 'screening',
            )
            if (!lead.pipeline_step_id && firstScreeningTab?.id === tab.id) {
                return tab.id
            }
            continue
        }

        if (
            tab.id === 'leases' ||
            (tab.step_type === 'lease' && tab.id === 'leases')
        ) {
            if (
                lead.pipeline_stage === 'lease_sent' ||
                lead.pipeline_stage === 'lease_signed'
            ) {
                return tab.id
            }
            continue
        }

        if (tab.id === 'lease_sent' || tab.id === 'lease_signed') {
            if (lead.pipeline_stage === tab.id) {
                return tab.id
            }
            continue
        }

        if (tab.stage === lead.pipeline_stage) {
            return tab.id
        }
    }

    return null
}

/**
 * Remap API count keys (config tabs, lease_sent/lease_signed, instanced
 * screening) onto the fixed demo tab ids used by the StageSwitcher.
 */
export function remapPipelineCountsToDemoTabs(
    counts: Record<string, number> | undefined | null,
): Record<string, number> {
    const result: Record<string, number> = {
        leads: 0,
        app_sent: 0,
        screening: 0,
        leases: 0,
        future_resident: 0,
    }
    if (!counts) {
        return result
    }

    for (const [tabId, count] of Object.entries(counts)) {
        if (!count) {
            continue
        }
        if (tabId === 'leads') {
            result.leads += count
        } else if (tabId === 'app_sent') {
            result.app_sent += count
        } else if (
            tabId === 'screening' ||
            tabId.startsWith('step_screening')
        ) {
            result.screening += count
        } else if (
            tabId === 'leases' ||
            tabId === 'lease_sent' ||
            tabId === 'lease_signed'
        ) {
            result.leases += count
        } else if (tabId === 'future_resident') {
            result.future_resident += count
        }
    }

    return result
}
